import { createContext, useState, useContext, useEffect, ReactNode, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { logDebug, logError, logWarn, logInfo } from "@/utils/logger";
import { getAuthenticatedUser, setSkipAnonymousSession } from "@/utils/authUser";
import { getHomeFallbackPath } from "@/utils/whiteLabelStore";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  userRole: string | null;
  roleLoading: boolean;
  signIn: (email: string, password: string, origin?: string) => Promise<{ error: any; requiresPasswordChange?: boolean }>;
  signUp: (email: string, password: string, fullName: string, phone?: string) => Promise<{ error: any, data: any }>;
  signUpSupplier: (email: string, password: string, supplierData: any) => Promise<{ error: any, data: any }>;
  signInWithProvider: (provider: 'google' | 'facebook' | 'apple') => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any; method?: string; message?: string }>;
  signOut: () => Promise<void>;
  getRedirectUrlByRole: (role: string, origin?: string, userId?: string) => Promise<string>;
  saveSimulationData: (data: any) => void;
  linkSimulationToUser: () => Promise<void>;
};

// Create context with default values to prevent undefined context error
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  userRole: null,
  roleLoading: false,
  signIn: async () => ({ error: null, requiresPasswordChange: false }),
  signUp: async () => ({ error: null, data: null }),
  signUpSupplier: async () => ({ error: null, data: null }),
  signInWithProvider: async () => ({ error: null }),
  resetPassword: async () => ({ error: null, method: 'default', message: 'default' }),
  signOut: async () => {},
  getRedirectUrlByRole: async () => "/",
  saveSimulationData: () => {},
  linkSimulationToUser: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const { toast } = useToast();

  // Flags to prevent unnecessary redirects
  const hasRedirectedRef = useRef(false);
  const isPageVisibleRef = useRef(true);
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => {
    let mounted = true;

    // Simplified initialization - no deferred processing to prevent loops
    const initAuth = async () => {
      try {
        let { data: { session }, error } = await supabase.auth.getSession();

        if (!mounted) return;

        logDebug('Initial session check completed', {
          component: 'AuthContext',
          metadata: {
            hasSession: !!session,
            hasUser: !!session?.user,
            hasError: !!error
          }
        });

        // On the password reset page we KEEP the session in state (needed for
        // supabase.auth.updateUser), but flag the flow so role-based redirects
        // and SIGNED_IN side-effects are skipped.
        const isPasswordResetPage = window.location.pathname.includes('/nova-senha');

        if (isPasswordResetPage) {
          logInfo('initAuth: on /nova-senha - keeping session, skipping redirects', { component: 'AuthContext' });
          sessionStorage.setItem('password_reset_in_progress', 'true');
          sessionRef.current = session;
          setSession(session);
          setUser(getAuthenticatedUser(session?.user ?? null));
          setIsLoading(false);
          // Do NOT redirect, do NOT fetch role here
          return;
        }

        // Check if user needs to change password (first login with temp password)
        if (session?.user && !session.user.is_anonymous && session.user.user_metadata?.needs_password_change) {
          // Verify with server to avoid stale JWT metadata
          const { data: freshSession } = await supabase.auth.refreshSession();
          if (freshSession?.session?.user?.user_metadata?.needs_password_change) {
            logInfo('initAuth: user needs password change - redirecting to /nova-senha', { component: 'AuthContext' });
            sessionStorage.setItem('force_password_change', 'true');
            sessionStorage.setItem('password_reset_in_progress', 'true');
            sessionRef.current = freshSession.session;
            setIsLoading(false);
            window.location.href = '/nova-senha';
            return;
          }
          // Flag was cleared server-side, continue with refreshed session
          session = freshSession?.session ?? session;
        }

        sessionRef.current = session;
        setSession(session);
        setUser(getAuthenticatedUser(session?.user ?? null));
        setIsLoading(false);

        // Fetch role immediately if user exists
        if (session?.user && !session.user.is_anonymous) {
          await fetchUserRole(session.user.id);
        } else {
          setUserRole(null);
        }
      } catch (error) {
        logError('Error in initial auth setup', error as Error, { component: 'AuthContext' });
        setIsLoading(false);
      }
    };

    // Set up auth state listener with simplified logic
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        // OPTIMIZATION: Skip TOKEN_REFRESHED events if same user to prevent unnecessary re-renders
        if (event === 'TOKEN_REFRESHED' && session?.user?.id === sessionRef.current?.user?.id) {
          logDebug('Token refreshed for same user - skipping state update', {
            component: 'AuthContext',
            metadata: { event }
          });
          sessionRef.current = session;
          return;
        }

        // Intercept PASSWORD_RECOVERY: do NOT set user/session in React state
        // Intercept PASSWORD_RECOVERY: send the user to /nova-senha but KEEP the
        // session in state so the page can call supabase.auth.updateUser.
        if (event === 'PASSWORD_RECOVERY') {
          logInfo('PASSWORD_RECOVERY event intercepted', { component: 'AuthContext' });
          sessionStorage.setItem('password_reset_in_progress', 'true');
          sessionRef.current = session;
          setSession(session);
          setUser(getAuthenticatedUser(session?.user ?? null));
          setIsLoading(false);
          if (!window.location.pathname.includes('/nova-senha')) {
            window.location.href = '/nova-senha';
          }
          return;
        }

        // OPTIMIZATION: Check if session actually changed before updating state
        const isSameUser = session?.user?.id === sessionRef.current?.user?.id;
        const isSameAccessToken = session?.access_token === sessionRef.current?.access_token;

        if (isSameUser && isSameAccessToken && event !== 'SIGNED_OUT' && event !== 'SIGNED_IN') {
          logDebug('Same session detected - skipping state update', {
            component: 'AuthContext',
            metadata: { event }
          });
          return;
        }

        // Ao voltar de outra aba/janela o Supabase renova o token e reemite
        // SIGNED_IN para o MESMO usuário. Isso não é um novo login: tratamos
        // como renovação silenciosa (sem setSession/setUser, sem toast), para
        // não desmontar telas com formulários abertos.
        if (event === 'SIGNED_IN' && isSameUser && sessionRef.current) {
          logDebug('SIGNED_IN for same user (token refresh) - skipping state update', {
            component: 'AuthContext',
            metadata: { event }
          });
          sessionRef.current = session;
          return;
        }


        logInfo('Auth state changed', {
          component: 'AuthContext',
          metadata: {
            event,
            hasSession: !!session,
            hasUser: !!session?.user
          }
        });

        sessionRef.current = session;
        setSession(session);
        setUser(getAuthenticatedUser(session?.user ?? null));
        setIsLoading(false);

        // Debounced role fetching to prevent loops
        if (session?.user && !session.user.is_anonymous && event !== 'SIGNED_OUT') {
          // Only fetch if we don't already have a role for this user
          const cached = roleCache.current.get(session.user.id);
          if (!cached || Date.now() - cached.timestamp >= ROLE_CACHE_DURATION) {
            setRoleLoading(true);
            setTimeout(() => fetchUserRole(session.user.id), 100);
          } else {
            setUserRole(cached.role);
          }
        } else {
          setUserRole(null);
          roleCache.current.clear(); // Clear cache on logout
        }

        // Handle events with simple logic
        if (event === 'SIGNED_IN' && session?.user && !session.user.is_anonymous) {
          // Avoid async auth calls inside onAuthStateChange; rely on the current session metadata
          // and let /nova-senha validate/refresh the session when needed.
          if (session.user.user_metadata?.needs_password_change) {
            logInfo('User needs password change - redirecting to /nova-senha', { component: 'AuthContext' });
            sessionStorage.setItem('force_password_change', 'true');
            sessionStorage.setItem('password_reset_in_progress', 'true');
            return;
          }

          // Skip toast/redirect/simulation during password reset flow
          if (sessionStorage.getItem('password_reset_in_progress') === 'true') {
            logDebug('Skipping SIGNED_IN side-effects during password reset', { component: 'AuthContext' });
            return;
          }

          logInfo('User signed in', { component: 'AuthContext' });

          // Record login for tracking
          // Record login asynchronously - don't block the flow
          supabase.from('supplier_login_logs').insert({
            user_id: session.user.id,
            user_agent: navigator.userAgent,
          }).then(({ error: loginLogError }) => {
            if (loginLogError && !loginLogError.message?.includes('abort')) {
              console.warn('[AuthContext] Failed to record login log:', loginLogError.message);
            }
          }).catch((err: any) => {
            if (!err?.message?.includes('abort')) {
              console.warn('[AuthContext] Login log exception:', err);
            }
          });

          // Simple simulation linking without complex deferred logic
          try {
            await linkSimulationToUser();
          } catch (error) {
            logError('Error linking simulation', error as Error, { component: 'AuthContext' });
          }

          toast({
            title: "Login bem-sucedido",
            description: "Você está conectado à sua conta.",
          });
        } else if (event === 'SIGNED_OUT') {
          // Skip toast during password reset flow
          if (sessionStorage.getItem('password_reset_in_progress') === 'true') {
            logDebug('Skipping SIGNED_OUT toast during password reset', { component: 'AuthContext' });
            return;
          }

          logInfo('User signed out', { component: 'AuthContext' });
          toast({
            title: "Desconectado",
            description: "Você saiu da sua conta.",
          });
        }
      }
    );

    // Initialize auth
    initAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [toast]);

  // Separate useEffect for handling redirections based on auth/session changes
  useEffect(() => {
    // Don't redirect if OAuth params are present — let provider callback finish first
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('code') || searchParams.has('error')) {
      logInfo('OAuth params detected, skipping auth redirect', { component: 'AuthContext', action: 'oauth_guard' });
      return;
    }

    const currentPath = window.location.pathname;
    const isAdminPage = currentPath.startsWith('/admin');
    const isSupplierPage = currentPath.startsWith('/fornecedor');
    const isPasswordResetPage = currentPath.includes('/nova-senha');

    if (isPasswordResetPage) {
      // Never auto-redirect away from /nova-senha; the page itself controls the flow.
      return;
    }

    if (isAdminPage || isSupplierPage) {
      if (user) {
        localStorage.removeItem('postLoginRedirect');
      }
      localStorage.removeItem('loginOrigin');
      localStorage.removeItem('returnToForm');
      hasRedirectedRef.current = false;
      return;
    }

    if (user && !isLoading && isPageVisibleRef.current && !hasRedirectedRef.current) {
      let origin = localStorage.getItem('loginOrigin');
      // Origem de login esquecida (cliente abriu o login em infos-festa/simulação e saiu): sem esta checagem,
      // QUALQUER página aberta depois — cardápio, loja — jogava o cliente para a home. Só vale origem recente.
      if (origin) {
        const originAt = Number(localStorage.getItem('loginOriginAt') || 0);
        const fresh = originAt > 0 && Date.now() - originAt < 30 * 60 * 1000;
        if (!fresh) {
          localStorage.removeItem('loginOrigin');
          localStorage.removeItem('loginOriginAt');
          localStorage.removeItem('returnToForm');
          origin = null;
        }
      }
      const returnToForm = localStorage.getItem('returnToForm');

      if (userRole && origin) {
        if ((origin === 'infos-festa' || returnToForm === 'true') && window.location.pathname.includes('infos-festa')) {
          logInfo('Staying on infos-festa as requested', {
            component: 'AuthContext',
            action: 'stay_on_page',
            metadata: { origin, returnToForm }
          });
          localStorage.removeItem('loginOrigin');
          localStorage.removeItem('returnToForm');
          return;
        }

        hasRedirectedRef.current = true;
        localStorage.removeItem('loginOrigin');
        localStorage.removeItem('returnToForm');

        getRedirectUrlByRole(userRole, origin, user.id).then(redirectUrl => {
          if (!(origin === 'simulation' && userRole === 'client') && !(origin === 'infos-festa' && window.location.pathname.includes('infos-festa'))) {
            setTimeout(() => {
              if (isPageVisibleRef.current) {
                window.location.href = redirectUrl;
              }
            }, 100);
          } else {
            hasRedirectedRef.current = false;
          }
        }).catch(error => {
          logError('Error getting redirect URL', error as Error, { component: 'AuthContext' });
          hasRedirectedRef.current = false;
        });
      }
    }
  }, [user, userRole, isLoading]);

  // Role cache to prevent repeated fetches
  const roleCache = useRef<Map<string, { role: string; timestamp: number }>>(new Map());
  const ROLE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const fetchUserRole = async (userId: string) => {
    // Check cache first
    const cached = roleCache.current.get(userId);
    if (cached && Date.now() - cached.timestamp < ROLE_CACHE_DURATION) {
      logDebug('Using cached role', { component: 'AuthContext', action: 'fetchUserRole' });
      setUserRole(cached.role);
      return;
    }

    // Prevent concurrent requests
    if (roleLoading) {
      logDebug('Role already loading, skipping duplicate call', { component: 'AuthContext' });
      return;
    }

    setRoleLoading(true);

    try {
      const { data: role, error } = await supabase
        .rpc('get_user_role', { _user_id: userId });

      if (error) {
        // Ignore AbortError (happens during navigation) and "no rows" error
        const isAbort = error.message?.includes('abort') || error.name === 'AbortError';
        if (error.code === 'PGRST116' || isAbort) {
          logDebug('Role fetch aborted or no rows, will retry', { component: 'AuthContext' });
        } else {
          logError('Error fetching user role', error as Error, { component: 'AuthContext' });
        }
        // Don't cache errored role - retry next time
        setRoleLoading(false);
        // On abort, schedule a retry
        if (isAbort) {
          setTimeout(() => fetchUserRole(userId), 500);
        }
        return;
      }

      const resolvedRole = role || 'client';

      // Cache the result
      roleCache.current.set(userId, { role: resolvedRole, timestamp: Date.now() });

      logDebug('User role fetched and cached', { component: 'AuthContext', action: 'fetchUserRole' });
      setUserRole(resolvedRole);
    } catch (error: any) {
      const isAbort = error?.message?.includes('abort') || error?.name === 'AbortError';
      if (isAbort) {
        logDebug('fetchUserRole aborted, scheduling retry', { component: 'AuthContext' });
        setRoleLoading(false);
        setTimeout(() => fetchUserRole(userId), 500);
        return;
      }
      logError('Error in fetchUserRole', error as Error, { component: 'AuthContext' });
      // Don't cache or set a fallback role on error - leave userRole as null
    } finally {
      setRoleLoading(false);
    }
  };

  const getRedirectUrlByRole = async (role: string, origin?: string, userId?: string) => {
    if ((origin === 'simulation' || origin === 'infos-festa') && role === 'client') {
      return '/infos-festa';
    }

    switch (role) {
      case 'admin':
        return '/admin';
      case 'supplier':
        if (userId) {
          try {
            const { isSupplierApproved } = await import('@/utils/supabase-query');
            const approved = await isSupplierApproved(userId);

            if (!approved) {
              await supabase.auth.signOut();
              return '/?message=awaiting-approval';
            }
          } catch (error) {
            logError('Error checking supplier approval', error as Error, { component: 'AuthContext' });
            await supabase.auth.signOut();
            return '/?message=awaiting-approval';
          }
        }
        return '/fornecedor/painel/inicio';
      case 'partner':
        return '/parceiro/painel';
      case 'client':
        return '/';
      default:
        toast({
          title: "Acesso não autorizado",
          description: "Seu tipo de usuário não foi reconhecido. Entre em contato com o suporte.",
          variant: "destructive"
        });
        return '/';
    }
  };

  const saveSimulationData = (data: any) => {
    localStorage.setItem('simulationData', JSON.stringify(data));
  };

  const linkSimulationToUser = async () => {
    const sessionId = localStorage.getItem('simulation-session-id');
    const simulationData = localStorage.getItem('simulationData');

    // Only attempt linking if we have both sessionId and user, and there's actual simulation data
    if (sessionId && user && simulationData) {
      try {
        logDebug('Attempting to link simulation session to user', {
          component: 'AuthContext',
          action: 'linkSimulationToUser',
          metadata: {
            hasSessionId: !!sessionId,
            hasUser: !!user
          }
        });

        const { linkSessionToUser } = await import('@/services/simulationService');
        await linkSessionToUser(sessionId, user.id);

        logInfo('Simulation linked successfully', {
          component: 'AuthContext',
          action: 'linkSimulationToUser'
        });

        // Clear simulation data after successful linking
        localStorage.removeItem('simulation-session-id');
        localStorage.removeItem('simulationData');
      } catch (error) {
        logError('Error linking simulation to user', error as Error, {
          component: 'AuthContext',
          action: 'linkSimulationToUser'
        });
      }
    } else {
      // Only log if we actually have a sessionId (avoid spam logs)
      if (sessionId) {
        logDebug('Skipping simulation link - no simulation data found', {
          component: 'AuthContext',
          action: 'linkSimulationToUser'
        });
      }
    }
  };

  const signIn = async (email: string, password: string, origin?: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!error && data?.user) {
        setSkipAnonymousSession(false);
        // Check if user needs to change password (first login with temp password)
        if (data.user.user_metadata?.needs_password_change) {
          // Store flag so NovaSenhaPage knows it's a forced change
          sessionStorage.setItem('force_password_change', 'true');
          sessionStorage.setItem('password_reset_in_progress', 'true');
          return { error: null, requiresPasswordChange: true };
        }

        if (origin) {
          localStorage.setItem('loginOrigin', origin);
          localStorage.setItem('loginOriginAt', String(Date.now()));
        }
      }

      return { error, requiresPasswordChange: false };
    } catch (error) {
      return { error, requiresPasswordChange: false };
    }
  };

  const signUp = async (email: string, password: string, fullName: string, phone?: string) => {
    try {
      const userData: any = {
        full_name: fullName,
      };

      if (phone) {
        userData.phone = phone.replace(/\D/g, ''); // Remove formatting for storage
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/`
        },
      });

      return { data, error };
    } catch (error) {
      return { error, data: null };
    }
  };

  const signInWithProvider = async (provider: 'google' | 'facebook' | 'apple') => {
    try {
      // Environment detection and comprehensive debugging
      const currentUrl = window.location.href;
      const currentOrigin = window.location.origin;
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const isPreviewEnvironment = currentOrigin.includes('preview--ameup.lovable.app') ||
                                   currentOrigin.includes('.lovableproject.com');
      const isProductionEnvironment = currentOrigin.includes('ameup.com.br');
      const isInSimulation = currentPath.includes('infos-festa');
      const simulationId = new URLSearchParams(currentSearch).get('simulationId');

      logDebug('OAuth Debug Info', {
        component: 'AuthContext',
        action: 'signInWithProvider',
        metadata: {
          isPreviewEnvironment,
          isProductionEnvironment,
          isInSimulation,
          hasSimulationId: !!simulationId
        }
      });

      if (isInSimulation) {
        // Only set loginOrigin if not already set by caller (e.g., InfosFesta sets 'infos-festa')
        if (!localStorage.getItem('loginOrigin')) {
          localStorage.setItem('loginOrigin', 'simulation');
          localStorage.setItem('loginOriginAt', String(Date.now()));
        }
        if (simulationId) {
          localStorage.setItem('pendingSimulationId', simulationId);
        }
      }

      // Build redirect URL - use postLoginRedirect if set, otherwise current origin
      const savedRedirect = localStorage.getItem('postLoginRedirect');
      const redirectTo = isInSimulation
        ? `${currentOrigin}/infos-festa${currentSearch}` // Keep all URL parameters
        : savedRedirect
          ? `${currentOrigin}${savedRedirect.startsWith('/') ? savedRedirect : `/${savedRedirect}`}`
          : `${currentOrigin}/`;

      logDebug('OAuth redirect URL configured', {
        component: 'AuthContext',
        action: 'signInWithProvider',
        metadata: {
          redirectConfigured: true
        }
      });

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo
        }
      });

      if (error) {
        logError('Error signing in with provider', error as Error, {
          component: 'AuthContext',
          action: 'signInWithProvider'
        });
        toast({
          title: "Erro ao fazer login",
          description: "Tente novamente.",
          variant: "destructive"
        });
      }

      return { error };
    } catch (error) {
      logError('Error in signInWithProvider', error as Error, {
        component: 'AuthContext',
        action: 'signInWithProvider'
      });
      toast({
        title: "Erro ao fazer login",
        description: "Tente novamente.",
        variant: "destructive"
      });
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    logInfo('Iniciando processo de reset de senha', {
      component: 'AuthContext',
      action: 'resetPassword',
      metadata: { hasEmail: !!email }
    });

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/nova-senha`
      });

      if (error) {
        logError('Erro ao enviar email via Supabase SMTP', error, {
          component: 'AuthContext',
          action: 'resetPassword'
        });
        return { error: { message: error.message || 'Não foi possível enviar o email de redefinição.' } };
      }

      logInfo('Reset de senha enviado com sucesso via Supabase SMTP', {
        component: 'AuthContext',
        action: 'resetPassword',
        metadata: { method: 'supabase_smtp' }
      });

      return { error: null, method: 'supabase_smtp' };
    } catch (error: any) {
      logError('Erro ao enviar reset de senha', error, {
        component: 'AuthContext',
        action: 'resetPassword'
      });
      return {
        error: {
          message: error.message || 'Não foi possível enviar o email de redefinição. Tente novamente mais tarde.',
        }
      };
    }
  };

  const signUpSupplier = async (email: string, password: string, supplierData: any) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: supplierData.contactName,
            phone: supplierData.phone,
          },
          emailRedirectTo: `${window.location.origin}/fornecedor/painel/inicio`
        },
      });

      if (authError) return { error: authError, data: null };

      if (authData.user) {
        // Update profile with supplier role and phone
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            role: 'supplier',
            phone: supplierData.phone,
            updated_by: authData.user.id,
          })
          .eq('id', authData.user.id);

        if (profileError) return { error: profileError, data: null };

        const serviceTypes = supplierData.categories.join(', ');

        // Create supplier record (normalized schema - only core fields in suppliers table)
        const { data: supplierRecord, error: supplierError } = await supabase
          .from('suppliers')
          .insert({
            id: crypto.randomUUID(),
            user_id: authData.user.id,
            name: supplierData.companyName,
            document_number: supplierData.cnpj,
            legal_representative: supplierData.contactName,
            description: supplierData.description,
            service_type: serviceTypes,
            contact_email: email,
            contact_phone: supplierData.phone,
            instagram: supplierData.instagram || null,
          })
          .select('id')
          .single();

        if (supplierError) return { error: supplierError, data: null };

        const newSupplierId = supplierRecord.id;

        const [statusResult, addressResult] = await Promise.all([
          supabase.from('supplier_status').insert({
            id: crypto.randomUUID(),
            supplier_id: newSupplierId,
            is_approved: false,
          }),
          supabase.from('supplier_address').insert({
            id: crypto.randomUUID(),
            supplier_id: newSupplierId,
            address_name: supplierData.address || null,
            address_code: supplierData.zipCode || null,
            city: supplierData.city || null,
            state: supplierData.state || null,
          }),
        ]);

        if (statusResult.error) console.error('Error inserting supplier_status:', statusResult.error);
        if (addressResult.error) console.error('Error inserting supplier_address:', addressResult.error);
      }

      return { data: authData, error: null };
    } catch (error) {
      return { error, data: null };
    }
  };

  const signOut = async (): Promise<void> => {
    setSkipAnonymousSession(true);
    sessionRef.current = null;
    setSession(null);
    setUser(null);
    setUserRole(null);
    setIsLoading(false);
    roleCache.current.clear();

    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        logError('Supabase signOut error', error as Error, { component: 'AuthContext' });
      }
    } catch (error) {
      logError('Error during signOut', error as Error, { component: 'AuthContext' });
    }

    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sb-') && key.includes('-auth-token')) {
        localStorage.removeItem(key);
      }
    }

    // Em hosts white-label não podemos cair na home institucional da AmeUp.
    window.location.replace(getHomeFallbackPath());
  };

  const contextValue: AuthContextType = {
    user,
    session,
    isLoading,
    userRole,
    roleLoading,
    signIn,
    signUp,
    signUpSupplier,
    signInWithProvider,
    resetPassword,
    signOut,
    getRedirectUrlByRole,
    saveSimulationData,
    linkSimulationToUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}